<template>
  <v-container>
    <v-row class="mb-4">
      <v-col cols="12" md="6">
        <v-autocomplete
          v-model="selectedLift"
          :items="lift_list"
          item-text="lift_name"
          item-value="lift_id"
          label="Sélectionner une remontée"
          prepend-inner-icon="mdi-gondola"
          outlined
          @change="loadPeriodicChecks"
        ></v-autocomplete>
      </v-col>
      <v-col cols="12" md="6">
        <v-select
          v-model="filterInterval"
          :items="intervalFilterOptions"
          item-text="label"
          item-value="value"
          label="Filtrer par fréquence"
          clearable
          outlined
          dense
        ></v-select>
      </v-col>
    </v-row>

    <v-divider class="mb-4"></v-divider>

    <!-- Status Cards -->
    <v-row v-if="selectedLift && !isLoading">
      <v-col cols="12">
        <h2 class="mb-4">État des contrôles périodiques</h2>
      </v-col>
      <v-col 
        v-for="status in groupedByCategory" 
        :key="status.category"
        cols="12"
      >
        <v-card>
          <v-card-title class="primary white--text">
            <v-icon left color="white">mdi-calendar-check</v-icon>
            {{ status.category }}
          </v-card-title>
          <v-card-text>
            <v-simple-table>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th>Contrôle</th>
                    <th>Dernier contrôle</th>
                    <th>Par</th>
                    <th>Prochain contrôle</th>
                    <th>État</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="check in status.checks" :key="check.check_id">
                    <td>
                      <strong>{{ check.check_name }}</strong>
                      <br>
                      <span class="text-caption grey--text">Tous les {{ check.interval_days }} jours</span>
                    </td>
                    <td>{{ check.last_check_date ? formatDate(check.last_check_date) : 'Jamais effectué' }}</td>
                    <td>{{ check.performed_by_name || '-' }}</td>
                    <td>{{ check.next_check_date ? formatDate(check.next_check_date) : '-' }}</td>
                    <td>
                      <v-chip
                        :color="getStatusColor(check)"
                        dark
                        small
                      >
                        <v-icon left small>{{ getStatusIcon(check) }}</v-icon>
                        {{ getStatusText(check) }}
                      </v-chip>
                    </td>
                    <td>
                      <v-btn
                        icon
                        color="primary"
                        @click="openRecordDialog(check)"
                        :title="'Effectuer le contrôle'"
                      >
                        <v-icon>mdi-clipboard-check</v-icon>
                      </v-btn>
                      <v-btn
                        icon
                        color="info"
                        @click="openHistoryDialog(check)"
                        :title="'Voir l\'historique'"
                      >
                        <v-icon>mdi-history</v-icon>
                      </v-btn>
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Loading -->
    <v-row v-if="isLoading">
      <v-col cols="12" class="text-center">
        <v-progress-circular
          indeterminate
          color="primary"
          size="64"
        ></v-progress-circular>
      </v-col>
    </v-row>

    <!-- No Lift Selected -->
    <v-row v-if="!selectedLift && !isLoading">
      <v-col cols="12" class="text-center">
        <v-icon size="100" color="grey lighten-1">mdi-gondola</v-icon>
        <p class="text-h6 grey--text mt-4">Sélectionnez une remontée pour voir ses contrôles périodiques</p>
      </v-col>
    </v-row>

    <!-- Dialog: Add/Edit Check -->
    <v-dialog v-model="dialogAddCheck" max-width="600px">
      <v-card>
        <v-card-title>
          <span class="text-h5">{{ editingCheck ? 'Modifier' : 'Ajouter' }} un contrôle périodique</span>
        </v-card-title>
        <v-card-text>
          <v-form ref="formAddCheck" v-model="isFormValid">
            <v-text-field
              v-model="checkForm.check_name"
              label="Nom du contrôle"
              :rules="[v => !!v || 'Requis']"
              outlined
            ></v-text-field>
            
            <v-select
              v-model="checkForm.check_category"
              :items="categoryOptions"
              label="Catégorie"
              :rules="[v => !!v || 'Requis']"
              outlined
            ></v-select>
            
            <v-select
              v-model="checkForm.interval_days"
              :items="intervalOptions"
              item-text="label"
              item-value="value"
              label="Fréquence"
              :rules="[v => !!v || 'Requis']"
              outlined
            ></v-select>
            
            <v-textarea
              v-model="checkForm.description"
              label="Description"
              outlined
              rows="3"
            ></v-textarea>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="dialogAddCheck = false">Annuler</v-btn>
          <v-btn color="primary" @click="saveCheck" :disabled="!isFormValid">Enregistrer</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: Record Check -->
    <v-dialog v-model="dialogRecordCheck" max-width="600px">
      <v-card>
        <v-card-title>
          <span class="text-h5">Effectuer le contrôle</span>
        </v-card-title>
        <v-card-text>
          <v-form ref="formRecordCheck" v-model="isRecordFormValid">
            <h3 class="mb-2">{{ selectedCheckForRecord?.check_name }}</h3>
            <p class="text-caption grey--text mb-4">{{ selectedCheckForRecord?.check_category }}</p>
            
            <v-alert type="info" dense text class="mb-4">
              Date du contrôle : {{ formatDate(new Date().toISOString().substr(0, 10)) }}
            </v-alert>
            
            <v-switch
              v-model="recordForm.is_compliant"
              label="Contrôle conforme"
              color="success"
            ></v-switch>
            
            <v-textarea
              v-model="recordForm.comment"
              label="Commentaire"
              outlined
              rows="3"
            ></v-textarea>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="dialogRecordCheck = false">Annuler</v-btn>
          <v-btn color="primary" @click="saveRecord" :disabled="!isRecordFormValid">Valider</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: History -->
    <v-dialog v-model="dialogHistory" max-width="800px">
      <v-card>
        <v-card-title>
          <span class="text-h5">Historique des contrôles</span>
        </v-card-title>
        <v-card-text>
          <h3 class="mb-4">{{ selectedCheckForHistory?.check_name }}</h3>
          <v-data-table
            :headers="historyHeaders"
            :items="$store.state.periodicChecksStore.checkHistory"
            :items-per-page="10"
            class="elevation-1"
          >
            <template v-slot:item.date_performed="{ item }">
              {{ formatDate(item.date_performed) }}
            </template>
            <template v-slot:item.is_compliant="{ item }">
              <v-chip :color="item.is_compliant ? 'success' : 'error'" dark small>
                {{ item.is_compliant ? 'Conforme' : 'Non conforme' }}
              </v-chip>
            </template>
            <template v-slot:item.next_check_date="{ item }">
              {{ formatDate(item.next_check_date) }}
            </template>
          </v-data-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="dialogHistory = false">Fermer</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
export default {
  name: 'PeriodicChecks',
  data() {
    return {
      selectedLift: null,
      filterInterval: null,
      dialogAddCheck: false,
      dialogRecordCheck: false,
      dialogHistory: false,
      menuDatePicker: false,
      isFormValid: false,
      isRecordFormValid: false,
      editingCheck: null,
      selectedCheckForRecord: null,
      selectedCheckForHistory: null,
      checkForm: {
        check_name: '',
        check_category: '',
        interval_days: null,
        description: '',
      },
      recordForm: {
        date_performed: new Date().toISOString().substr(0, 10),
        is_compliant: true,
        comment: '',
      },
      categoryOptions: [
        'Véhicules',
        'Ligne',
        'Moteur'
      ],
      intervalOptions: [
        { label: 'Hebdomadaire (7 jours)', value: 7 },
        { label: 'Bi-mensuel (15 jours)', value: 15 },
        { label: 'Mensuel (30 jours)', value: 30 },
        { label: 'Trimestriel (90 jours)', value: 90 },
        { label: 'Semestriel (180 jours)', value: 180 },
        { label: 'Annuel (365 jours)', value: 365 },
      ],
      intervalFilterOptions: [
        { label: 'Hebdomadaire (7 jours)', value: 7 },
        { label: 'Bi-mensuel (15 jours)', value: 15 },
        { label: 'Mensuel (30 jours)', value: 30 },
        { label: 'Trimestriel (90 jours)', value: 90 },
        { label: 'Semestriel (180 jours)', value: 180 },
        { label: 'Annuel (365 jours)', value: 365 },
      ],
      historyHeaders: [
        { text: 'Date', value: 'date_performed' },
        { text: 'Effectué par', value: 'surname' },
        { text: 'État', value: 'is_compliant' },
        { text: 'Prochain contrôle', value: 'next_check_date' },
        { text: 'Commentaire', value: 'comment' },
      ],
    }
  },
  computed: {
    lift_list() {
      return this.$store.state.liftsStore.liftsList
    },
    isLoading() {
      return this.$store.state.periodicChecksStore.isLoading
    },
    periodicCheckStatus() {
      return this.$store.state.periodicChecksStore.periodicCheckStatus
    },
    groupedByCategory() {
      const grouped = {}
      const filteredChecks = this.filterInterval 
        ? this.periodicCheckStatus.filter(check => check.interval_days === this.filterInterval)
        : this.periodicCheckStatus
      
      filteredChecks.forEach(check => {
        if (!grouped[check.check_category]) {
          grouped[check.check_category] = {
            category: check.check_category,
            checks: []
          }
        }
        grouped[check.check_category].checks.push(check)
      })
      return Object.values(grouped)
    },
  },
  methods: {
    async loadPeriodicChecks() {
      if (this.selectedLift) {
        await this.$store.dispatch('getPeriodicCheckStatus', this.selectedLift)
      }
    },
    openAddCheckDialog() {
      this.editingCheck = null
      this.checkForm = {
        check_name: '',
        check_category: '',
        interval_days: null,
        description: '',
      }
      this.dialogAddCheck = true
    },
    async saveCheck() {
      if (this.$refs.formAddCheck.validate()) {
        try {
          await this.$store.dispatch('addPeriodicCheck', {
            ...this.checkForm,
            lifts_selected: [this.selectedLift],
          })
          this.dialogAddCheck = false
          this.$store.dispatch('addNotification', {
            message: 'Contrôle enregistré avec succès',
            type: 'success'
          })
        } catch (error) {
          this.$store.dispatch('addNotification', {
            message: 'Erreur lors de l\'enregistrement',
            type: 'error'
          })
        }
      }
    },
    openRecordDialog(check) {
      this.selectedCheckForRecord = check
      this.recordForm = {
        date_performed: new Date().toISOString().substr(0, 10),
        is_compliant: true,
        comment: '',
      }
      this.dialogRecordCheck = true
    },
    async saveRecord() {
      if (this.$refs.formRecordCheck.validate()) {
        try {
          await this.$store.dispatch('recordPeriodicCheck', {
            check_id: this.selectedCheckForRecord.check_id,
            lift_id: this.selectedLift,
            performed_by: this.$store.state.authStore.dataUser.userId,
            ...this.recordForm,
          })
          this.dialogRecordCheck = false
          this.$store.dispatch('addNotification', {
            message: 'Contrôle enregistré avec succès',
            type: 'success'
          })
        } catch (error) {
          this.$store.dispatch('addNotification', {
            message: 'Erreur lors de l\'enregistrement',
            type: 'error'
          })
        }
      }
    },
    async openHistoryDialog(check) {
      this.selectedCheckForHistory = check
      await this.$store.dispatch('getPeriodicCheckHistory', {
        check_id: check.check_id,
        lift_id: this.selectedLift,
      })
      this.dialogHistory = true
    },
    async deleteCheck(check) {
      if (confirm(`Êtes-vous sûr de vouloir supprimer le contrôle "${check.check_name}" ?`)) {
        try {
          await this.$store.dispatch('deletePeriodicCheck', {
            check_id: check.check_id,
            lift_id: this.selectedLift,
          })
          this.$store.dispatch('addNotification', {
            message: 'Contrôle supprimé',
            type: 'success'
          })
        } catch (error) {
          this.$store.dispatch('addNotification', {
            message: 'Erreur lors de la suppression',
            type: 'error'
          })
        }
      }
    },
    formatDate(dateString) {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleDateString('fr-FR', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      })
    },
    getStatusColor(check) {
      if (!check.last_check_date) return 'grey'
      if (check.is_overdue) return 'error'
      if (!check.is_compliant) return 'warning'
      return 'success'
    },
    getStatusIcon(check) {
      if (!check.last_check_date) return 'mdi-help-circle'
      if (check.is_overdue) return 'mdi-alert-circle'
      if (!check.is_compliant) return 'mdi-alert'
      return 'mdi-check-circle'
    },
    getStatusText(check) {
      if (!check.last_check_date) return 'Jamais effectué'
      if (check.is_overdue) return 'En retard'
      if (!check.is_compliant) return 'Non conforme'
      return 'Conforme'
    },
  },
  async mounted() {
    await this.$store.dispatch('getLiftsList', new Date().toISOString().substr(0, 10))
    
    // Si on vient depuis LiftRecord avec un lift_id
    if (this.$route.query.lift_id) {
      // Convertir en number si c'est une string, sinon utiliser directement
      const liftIdParam = this.$route.query.lift_id
      this.selectedLift = typeof liftIdParam === 'string' ? parseInt(liftIdParam) : liftIdParam
      await this.loadPeriodicChecks()
    }
  },
}
</script>

<style scoped>
.v-chip {
  font-weight: bold;
}
</style>
